
export enum ViewState {
  // Main Content Views
  REPORTS = 'REPORTS',
  PHRASES = 'PHRASES',
  RECYCLE_BIN = 'RECYCLE_BIN',
  DASHBOARD_USER = 'DASHBOARD_USER', // Nueva vista para el dashboard personal
  
  // Editor Views
  CREATE_REPORT = 'CREATE_REPORT',
  EDIT_REPORT = 'EDIT_REPORT',
  CREATE_PHRASE = 'CREATE_PHRASE',
  EDIT_PHRASE = 'EDIT_PHRASE',
  MANAGE_FILTERS = 'MANAGE_FILTERS',

  // Navigation Tab Views
  MODULAR_1 = 'MODULAR_1',
  MODULAR_2 = 'MODULAR_2',
  DICTAPHONE = 'DICTAPHONE',
  PREF = 'PREF', 
  CHAT_IA = 'CHAT_IA',
  COMMUNITY = 'COMMUNITY',
  PROFILE = 'PROFILE',
  AI_DRAFTING = 'AI_DRAFTING',
  ADMIN_PANEL = 'ADMIN_PANEL',
  AGENT = 'AGENT',
  PHD_ASSISTANCE = 'PHD_ASSISTANCE',
  LATENCY_CONTROL = 'LATENCY_CONTROL',
}

export interface SystemPricing {
  models: Record<string, { input: number; output: number; label: string; color: string }>;
  searchFlatFee: number;
  lastUpdated: number;
  billingStartDate?: number;
  roiConfig?: ROIConfig; // Configuración dinámica de tiempos de ahorro
  baseFeeDefault?: number;
}

export enum ROIEventType {
  GENERATION = 'GENERATION',
  GENERATE_RX = 'GENERATE_RX',
  GENERATE_TC = 'GENERATE_TC',
  GENERATE_RM = 'GENERATE_RM',
  PHD_AUDIT = 'PHD_AUDIT',
  AUDIT = 'AUDIT',
  TRANSLATION = 'TRANSLATION',
  CONCLUSION = 'CONCLUSION',
  REFINE = 'REFINE',
  SUMMARIZE = 'SUMMARIZE',
  SAVE_REPORT = 'SAVE_REPORT',
  IMPROVEMENT = 'IMPROVEMENT',
  STRUCTURE = 'STRUCTURE',
  NARRATIVE = 'NARRATIVE',
  OPTIMIZE = 'OPTIMIZE',
  AGENT_REPORT = 'AGENT_REPORT',
  TRANSCRIPTION = 'TRANSCRIPTION',
  PHD_ASSISTANCE = 'PHD_ASSISTANCE',
  RECONCILIATION = 'RECONCILIATION',
}

export interface AppSuggestion {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  text: string;
  timestamp: number;
  status: 'PENDING' | 'READ' | 'ARCHIVED';
}

export interface ModuleRating {
  id: string;
  userId: string;
  module: ViewState;
  rating: number; // 1-5
  timestamp: number;
}

export interface AppNotice {
  id: string;
  title: string;
  content: string;
  type: 'INFO' | 'WARNING' | 'CRITICAL';
  timestamp: number;
  active: boolean;
}

export interface ROIConfig {
  times: Record<ROIEventType, number>; // Tiempos en minutos
}

export interface ROIEvent {
  id: string;
  userId: string;
  type: ROIEventType;
  complexity: 'SIMPLE' | 'COMPLEX' | 'PLUS';
  hoursSaved: number;
  timestamp: number;
  metadata?: any;
}

export interface TokenUsageRecord {
  id: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  hasGrounding: boolean;
  timestamp: number;
  estimatedCost: number;
}

export interface UserActivityStats {
  reports: number;
  reportsByComplexity: {
    simple: number;
    complex: number;
    plus: number;
  };
  phrases: number;
  transcriptions: number;
  chats: number;
  drafting: number;
  phdAssistance: number;
  totalUsage: number;
  tokens: {
    totalInput: number;
    totalOutput: number;
    totalCost: number;
    searchCount: number;
    byModel: Record<string, { input: number; output: number; cost: number }>;
  };
  // Series temporales para gráficos
  hourlyROI: { label: string, hours: number, timestamp: number }[];
  weeklyROI: { label: string, hours: number, timestamp: number }[];
  dailyROI: { label: string, hours: number, timestamp: number }[];
  monthlyROI: { label: string, hours: number, timestamp: number }[];
  yearlyROI: { label: string, hours: number, timestamp: number }[];
  modelHistory: Record<string, { date: string, tokens: number }[]>;
  roiEvents: ROIEvent[];
}

export type ModularLayoutType = 'single' | 'split-vertical' | 'split-horizontal' | 'split-three' | 'split-four' | 'split-three-focus' | 'grid-2x2' | 'mixed' | 'mixed-mirror';
export type SidebarPosition = 'left' | 'right';

export interface PersistentWindow {
  view: ViewState;
  x: number;
  y: number;
  w: number;
  h: number;
  lastUpdated: number;
}

export interface UIState {
  lastUpdated: number;
  settings: Record<string, string>;
}

export interface Filter {
  id: string;
  name: string;
  isActive: boolean; 
  createdAt?: number;
}

export interface Category {
  id: string;
  name: string;
  color: string; 
  isVisible: boolean; 
  filters: Filter[];
  type: 'REPORT' | 'PHRASE';
  icon?: string; 
  createdAt?: number;
  order?: number;
}

export interface ReportTemplate {
  id: string;
  title: string;
  content: string; 
  filterIds: string[]; 
  isFavorite: boolean;
  usageCount: number;
  createdAt: number;
  updatedAt: number;
  isNewlyCreated?: boolean;
  deletedAt?: number; 
}

export interface Phrase {
  id: string;
  text: string;
  filterIds: string[];
  isFavorite: boolean;
  isFrequent: boolean;
  usageCount: number;
  createdAt: number;
  deletedAt?: number;
}

export interface SavedTranscription {
  id: string;
  text: string;
  history?: string[];
  versionIndex?: number;
  mode: 'ia' | 'manual';
  status?: 'success' | 'error' | 'processing';
  errorDetails?: string;
  backupId?: string;
  createdAt: number;
}

export interface ChatSession {
  id: string;
  userId: string;
  title: string;
  historial: GeminiContent[];
  createdAt: number;
  updatedAt: number;
}

export interface ChatSummary {
  id: string;
  sessionId: string;
  userId: string;
  title: string;
  content: string;
  tags: string[];
  createdAt: number;
}

export interface GeminiContent {
  role: 'user' | 'model';
  parts: { text: string }[]; 
  proParts?: { text: string }[]; 
  groundingMetadata?: any; 
  proGroundingMetadata?: any; 
}

export interface ChatSettings {
  absoluteMode: boolean;
  length: 'SHORT' | 'MEDIUM' | 'LONG';
}

export interface PeerMessage {
  id: string;
  senderId: string;
  text: string;
  timestamp: number;
  expiresAt?: number; 
  sharedContent?: {
    type: 'REPORT' | 'PHRASE' | 'SUMMARY' | 'IMAGE';
    data: any;
    title: string;
  };
}

export interface PeerChat {
  id: string;
  participants: string[];
  lastMessage: PeerMessage;
  updatedAt: number;
}

export interface UserMessage {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: number;
  read: boolean;
  type: 'VALIDATION' | 'SYSTEM' | 'PERSONAL';
}

export interface UserProfile {
  uid: string;
  displayName: string;
  email: string;
  role: UserRole;
  status: CollaborationStatus;
  mainSpecialty: string;
  specialties: string[];
  linkedin?: string;
  college?: string;
  collegeNumber?: string;
  dni?: string;
  country?: string;
  workplace?: string;
  workCity?: string;
  workCountry?: string;
  teleRadiologyInterest?: boolean;
  photoURL?: string | null;
  experienceYears?: string;
  residentYear?: number;
  otherMedicalSpecialty?: string;
  updatedAt: number;
  isVerified: boolean;
  isBlocked?: boolean;
  bypassProfileLock?: boolean;
  internalTestingBypass?: boolean;
  validationReport?: string;
  aiPreApproved?: boolean;
  aiConfidenceScore?: number;
  lastAuditedAt?: number;
  location?: { lat: number; lng: number };
  pinnedContacts?: string[];
  termsAcceptedAt?: number | null;
  termsVersion?: string | null;
  lastSeenAt?: number;
  customBaseFee?: number;
  notificationSettings?: NotificationSettings;
  dataRescueStatus?: 'PENDING' | 'COMPLETED' | 'DISMISSED';
}

export interface NotificationSettings {
  communityToasts: boolean;
  groupToasts: boolean;
  mutedGroups: string[];
}

export interface WorkGroup {
  id: string;
  name: string;
  description: string;
  members: string[];
  admins: string[];
  pendingAdminId?: string; // Para el traspaso de mando
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  photoURL?: string;
  lastMessage?: WorkGroupMessage;
  isPublic: boolean;
}

export interface GroupJoinRequest {
  id: string;
  groupId: string;
  groupName: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhoto?: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'CANCELLED';
  type: 'JOIN_REQUEST' | 'INVITATION';
  timestamp: number;
}

export interface WorkGroupMessage {
  id: string;
  groupId: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: number;
  sharedContent?: {
    type: 'REPORT' | 'PHRASE' | 'SUMMARY' | 'IMAGE';
    data: any;
    title: string;
  };
}

export type UserRole = 'RADIOLOGIST' | 'RESIDENT' | 'ADMIN';
export type CollaborationStatus = 'AVAILABLE' | 'READING' | 'ON_CALL' | 'NOT_AVAILABLE';

export enum LatencyModule {
  TRANSCRIPTION = 'TRANSCRIPTION',
  REPORT_GEN = 'REPORT_GEN',
  MODULAR_1_FUSION = 'MODULAR_1_FUSION',
  PHD_AUDIT = 'PHD_AUDIT',
  DIAGNOSTIC_ASSISTANT = 'DIAGNOSTIC_ASSISTANT'
}

export interface LatencyEvent {
  id: string;
  userId: string;
  module: LatencyModule;
  durationMs: number;
  payloadSize?: number; // Tamaño en bytes/caracteres
  status: 'SUCCESS' | 'ERROR' | 'TIMEOUT';
  errorCode?: string;
  errorMessage?: string;
  timestamp: number;
  metadata?: {
    model?: string;
    audioDuration?: number;
    retryCount?: number;
    networkType?: string;
  };
}

export interface RegionalRestriction {
  id: string;
  type: 'COUNTRY' | 'CONTINENT';
  value: string;
  disabledModules: string[];
  renameConclusionToSummary?: boolean;
  disableDiferenciales?: boolean;
  updatedAt: number;
}

export const LANGUAGES = ['Español', 'Catalán', 'Inglés', 'Alemán', 'Francés'];

export const DEFAULT_CATEGORIES: Category[] = [
  { id: 'cat1', name: 'MODALIDAD', color: 'text-blue-600', isVisible: true, type: 'REPORT', filters: [
    { id: 'f1', name: 'TC', isActive: true },
    { id: 'f2', name: 'RM', isActive: true },
    { id: 'f3', name: 'RX', isActive: true },
    { id: 'f4', name: 'US', isActive: true }
  ], order: 0 },
  { id: 'cat2', name: 'SISTEMA', color: 'text-indigo-600', isVisible: true, type: 'REPORT', filters: [
    { id: 'f5', name: 'NEURO', isActive: true },
    { id: 'f6', name: 'TÓRAX', isActive: true },
    { id: 'f7', name: 'ABDOMEN', isActive: true },
    { id: 'f8', name: 'MSK', isActive: true }
  ], order: 1 }
];

export const DEFAULT_REPORTS: ReportTemplate[] = [
  { id: 'r1', title: 'TC CRANEAL NORMAL', content: 'Estudio de TC craneal sin administración de contraste... <b>No se observan colecciones hemáticas intra ni extraaxiales.</b>', filterIds: ['f1', 'f5'], isFavorite: true, usageCount: 0, createdAt: Date.now(), updatedAt: Date.now() }
];

export const DEFAULT_PHRASES: Phrase[] = [
  { id: 'p1', text: 'Estudio realizado sin incidencias técnicas.', filterIds: [], isFavorite: false, isFrequent: true, usageCount: 0, createdAt: Date.now() }
];
